import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { authApi } from '@/lib/pillarApi';
import {
    PasswordPolicyFeedback,
    passwordPolicyBlocksSubmit,
    usePasswordPolicyFeedback,
} from '@/components/auth/PasswordPolicyFeedback';

export function ResetPasswordContent() {
    const params = useParams();
    const navigate = useNavigate();
    const token = params.token;
    if (token === undefined) {
        throw new Error('Reset password token is required');
    }
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const passwordPolicyState = usePasswordPolicyFeedback(password);
    const tokenValidationQuery = useQuery({
        queryKey: ['validatePasswordResetToken', token],
        queryFn: async () => await authApi.validatePasswordResetToken(token),
        retry: false,
    });

    const resetMutation = useMutation({
        mutationFn: () => authApi.resetPassword(token, password),
        onSuccess: () => {
            // Success - user can click button to go to login
        },
    });

    const validatePasswords = (): boolean => {
        setPasswordError('');

        if (!password) {
            setPasswordError('Password is required');
            return false;
        }

        if (passwordPolicyBlocksSubmit(passwordPolicyState, password)) {
            setPasswordError('Password does not satisfy the password policy');
            return false;
        }

        if (password !== confirmPassword) {
            setPasswordError('Passwords do not match');
            return false;
        }

        return true;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!validatePasswords()) {
            return;
        }

        resetMutation.mutate();
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <Card className="max-w-md w-full">
                <CardHeader>
                    <CardTitle className="text-center">Reset Password</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    {tokenValidationQuery.isPending && (
                        <div className="text-center text-sm text-muted-foreground">
                            Validating reset link...
                        </div>
                    )}

                    {tokenValidationQuery.isError && (
                        <>
                            <Alert variant="destructive">
                                <AlertDescription>
                                    <div className="font-semibold mb-2">Reset Link Invalid</div>
                                    <p className="text-sm">
                                        {tokenValidationQuery.error instanceof Error
                                            ? tokenValidationQuery.error.message
                                            : 'This password reset link is invalid, expired, or already used.'}
                                    </p>
                                </AlertDescription>
                            </Alert>

                            <div className="space-y-2">
                                <Button
                                    onClick={() => navigate('/forgot-password')}
                                    className="w-full"
                                >
                                    Request New Reset Link
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => navigate('/login')}
                                    className="w-full"
                                >
                                    Back to Login
                                </Button>
                            </div>
                        </>
                    )}

                    {resetMutation.isSuccess && (
                        <>
                            <Alert>
                                <AlertDescription className="text-center">
                                    <div className="text-green-600 font-semibold text-lg mb-2">
                                        Password Reset Successful
                                    </div>
                                    <p className="mb-2">{resetMutation.data.message}</p>
                                </AlertDescription>
                            </Alert>

                            <div className="space-y-4">
                                <p className="text-sm text-muted-foreground text-center">
                                    Your password has been reset successfully. You can now log in
                                    with your new password.
                                </p>
                                <Button onClick={() => navigate('/login')} className="w-full">
                                    Go to Login
                                </Button>
                            </div>
                        </>
                    )}

                    {!resetMutation.isSuccess && tokenValidationQuery.isSuccess && (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="password">New Password</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter your new password"
                                    disabled={resetMutation.isPending}
                                    required
                                />
                                <PasswordPolicyFeedback
                                    password={password}
                                    state={passwordPolicyState}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword">Confirm Password</Label>
                                <Input
                                    id="confirmPassword"
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Confirm your new password"
                                    disabled={resetMutation.isPending}
                                    required
                                />
                            </div>

                            {passwordError && (
                                <Alert variant="destructive">
                                    <AlertDescription>{passwordError}</AlertDescription>
                                </Alert>
                            )}

                            {resetMutation.isError && (
                                <Alert variant="destructive">
                                    <AlertDescription>
                                        <div className="font-semibold mb-2">Reset Failed</div>
                                        <p className="text-sm">
                                            {resetMutation.error instanceof Error
                                                ? resetMutation.error.message
                                                : 'Failed to reset password. The token may be invalid, expired, or already used.'}
                                        </p>
                                    </AlertDescription>
                                </Alert>
                            )}

                            <Button
                                type="submit"
                                className="w-full"
                                disabled={
                                    resetMutation.isPending ||
                                    passwordPolicyBlocksSubmit(passwordPolicyState, password)
                                }
                            >
                                {resetMutation.isPending ? 'Resetting...' : 'Reset Password'}
                            </Button>

                            {resetMutation.isPending && (
                                <div className="text-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                                </div>
                            )}
                        </form>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
